// OverviewSection.tsx
import * as React from "react";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import CtaButton from "../buttonWithArrow";
import Image from "next/image";

const MAX_W = 1080;   // section content cap
const CARD_MAX_W = 1300;  // headline text cap

export default function OverviewSection({ data, onClick, bgcolor }) {
    const { title, subtitle, cards = [], button } = data;

    // md+ grid width per card so all fit in one row
    const mdColsPerCard = React.useMemo(
        () => Math.max(1, Math.floor(12 / Math.max(1, cards.length))),
        [cards.length]
    );

    return (
        // pages with a fixed hero behind the content must pass an opaque bgcolor
        <Box component="section" sx={{ py: { xs: 8, md: 6 }, px: { xs: 2, sm: 3 }, bgcolor: bgcolor ?? "transparent" }}>

            {/* Header */}
            <Box sx={{ maxWidth: MAX_W, mx: "auto", mb: { xs: 6, md: 8 } }}>
                <Stack alignItems="center" spacing={1.25}>
                    <Typography
                        className='jura'
                        sx={{
                            fontSize: { xs: 14, md: 20 },
                            color: "text.secondary",
                            letterSpacing: ".06em",
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Anchor box for the heading + sparkle */}
                    <Box sx={{ position: "relative", width: "100%" }}>
                        <Typography
                            component="h2"
                            className='jura'
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: 28, md: 32 },
                                lineHeight: 1.2,
                                color: "#111",
                                textAlign: "center",
                                px: { xs: 2, md: 0 }, // tiny side padding helps on very small screens
                            }}
                        >
                            {subtitle}
                        </Typography>

                        {/* sparkle image (now positioned relative to this box) */}
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: { xs: -6, sm: -4, md: -20, lg: -15 },    // md unchanged; better placement on xs/sm
                                right: { xs: -6, sm: -8, md: 10, lg: -15, xl: 10 }, // md unchanged
                                width: { xs: 42, sm: 52, md: 50 },  // scale for small screens
                                height: { xs: 42, sm: 52, md: 50 },
                                pointerEvents: "none",
                                zIndex: 1,
                            }}
                        >
                            <Image
                                src="/img/investments/Sparkle.svg"
                                alt=""
                                fill
                                style={{ objectFit: "contain" }}
                                priority
                            />
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {/* Cards */}
            <Box sx={{ position: "relative", maxWidth: CARD_MAX_W, mx: "auto" }}>
                <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ mb: { xs: 5, md: 6 } }}>
                    {cards.map((c) => {
                        const isBottom = (c.arrow || "").toLowerCase().includes("bottom");
                        return (
                            <Grid
                                item
                                key={c.title}
                                xs={12}
                                sm={6}
                                md={mdColsPerCard}   // 👈 dynamic span at md+
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        position: "relative",
                                        height: "100%",
                                        minHeight: { xs: 180, md: 210 },
                                        p: { xs: 2.75, md: 3.25 },
                                        borderRadius: 3,
                                        border: "1px solid #e9edf2",
                                        backgroundColor: "#fff",
                                        boxShadow: "0 8px 8px rgba(0,0,0,.06)",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 20 }, mb: 1.25, color: "#0f1115" }}>
                                        {c.title}
                                    </Typography>

                                    <Typography sx={{ color: "#7d8895", fontSize: { xs: 14, md: 16 }, lineHeight: 1.55 }}>
                                        {c.body}
                                    </Typography>

                                    {c.arrow && (
                                        <Box
                                            component="img"
                                            src={c.arrow}
                                            alt=""
                                            aria-hidden="true"
                                            loading="lazy"
                                            sx={{
                                                position: "absolute",
                                                display: { xs: "none", md: "block" },
                                                pointerEvents: "none",
                                                width: { md: 88, lg: 96, xl: 110 },
                                                height: "auto",
                                                right: { md: -60, lg: -60, xl: -70 },
                                                zIndex: 1,
                                                ...(isBottom
                                                    ? { bottom: { md: 15, lg: 20, xl: 20 } }
                                                    : { top: { md: 25, lg: 30, xl: 25 } }),
                                            }}
                                        />
                                    )}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                {button && (
                    <Stack alignItems="center">
                        <CtaButton label={button?.text} onClick={() => onClick()} />
                        {/* <CtaButton cta={{ label: button?.text, href: button?.link }} /> */}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

